import { useEffect, useState, useContext } from 'react';
import Select from 'react-select';
import { ThemeContext } from '../context/ThemeContext';
import ReactDOM from 'react-dom';
import { getPackages } from '../services/api';
import api from '../services/api';
import { useProfile } from '../context/ProfileContext';
import { getCMS, getSettings } from '../services/cms';
import { submitBooking } from '../services/booking';

const CATEGORY_OPTIONS = [
  { key: 'delegate', label: 'Delegate', icon: '👤' },
  { key: 'exhibitor', label: 'Exhibitor', icon: '🏢' },
  { key: 'sponsorship', label: 'Sponsor', icon: '💼' },
  { key: 'speaker', label: 'Speaker', icon: '🎤' },
];

// Dynamically detect available categories from packages
function getAvailableCategories(packages) {
  const uniqueCategories = Array.from(new Set(packages.map(p => p.category)));
  return CATEGORY_OPTIONS.filter(opt => uniqueCategories.includes(opt.key));
}

// Helper to get only speaker packages
function getSpeakerPackages(packages) {
  return packages.filter(p => p.category === 'speaker');
}

export default function BookingModal({ open, onClose, onBookingAdded, manual }) {
  const { profile } = useProfile() || {};
  const currentUser = profile || (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || '{}') : {});
  // Example hotels for accommodation (replace with API call if needed)
  const hotels = [
    { id: 'h1', name: 'Serena Hotel', detail: '5-star, City Center' },
    { id: 'h2', name: 'Hyatt Regency', detail: 'Luxury, Waterfront' },
    { id: 'h3', name: 'Holiday Inn', detail: 'Business, Airport Area' },
  ];
  // (Removed duplicate handleSubmit function)

  // Handle document upload (placeholder logic)
  function handleDocUpload() {
    if (!docFile) return;
    setUploading(true);
    // Simulate upload
    setTimeout(() => {
      setDocUrl(URL.createObjectURL(docFile));
      setUploading(false);
    }, 1000);
  }
  // Handle booking form submission
  async function handleSubmit(e) {
    e.preventDefault();
    setErrorMsg("");
    setConfirmation(false);
    // Always ensure email is sent, not empty, and valid format
    const emailValue = email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailValue) {
      setErrorMsg("Email is required for booking. Please enter a valid email address.");
      return;
    }
    if (!emailRegex.test(emailValue)) {
      setErrorMsg("Please enter a valid email address (e.g. user@example.com).");
      return;
    }
    // Always ensure city is selected
    if (!city || city.trim() === "") {
      setErrorMsg("City is required.");
      return;
    }
    const formData = new FormData();
    // Always send email for backend compatibility
    formData.append('email', emailValue);
    formData.append('packageId', selectedPackage);
    formData.append('category', selectedCategory);
    formData.append('mobile', mobile);
    formData.append('country', country);
    formData.append('city', city);
    if (selectedCategory === 'speaker') {
      formData.append('name', companyDetails.name);
      formData.append('details', professionalInfo.details || '');
      portfolioLinks.forEach(link => formData.append('portfolioLinks[]', link));
      if (docFile) formData.append('file', docFile);
    } else {
      formData.append('bulkCount', bulkCount);
      formData.append('companyName', companyDetails.name);
      formData.append('registrationNo', companyDetails.regNo);
      formData.append('address', companyDetails.address);
      formData.append('title', professionalInfo.title);
      formData.append('department', professionalInfo.department);
      formData.append('accommodation', speakerOptions.accommodation);
      formData.append('airportPickup', speakerOptions.airportPickup);
      if (companyDetails.hotelId) formData.append('hotelId', companyDetails.hotelId);
      if (companyDetails.departureDate) formData.append('departureDate', companyDetails.departureDate);
      if (companyDetails.departureTime) formData.append('departureTime', companyDetails.departureTime);
      if (docFile) formData.append('file', docFile);
    }
    try {
      // Attach assigned staff id when set by admin
      if (selectedStaff && (selectedStaff.value || selectedStaff._id)) {
        formData.append('assignedStaffId', selectedStaff.value || selectedStaff._id);
      }
      // Attach creator metadata when available so backend can audit who created this booking
      if (currentUser && (currentUser._id || currentUser.id)) {
        formData.append('createdBy', currentUser._id || currentUser.id);
        formData.append('creatorRole', currentUser.role || '');
      }
      // Mark as admin/staff created when modal is used in manual/admin flow
      if (manual || (currentUser && ['admin', 'superadmin', 'staff'].includes(currentUser.role))) {
        formData.append('adminCreated', '1');
      }
      // Use submitBooking (axios) instead of fetch for consistency and error handling
      const data = await submitBooking(formData);
      setBookingId(data._id || data.id || '');
      setConfirmation(true);
      // Reset form fields after success
      setStep(1);
      setSelectedCategory('delegate');
      setSelectedPackage('');
      setBulkCount(1);
      setDocFile(null);
      setDocUrl(null);
      setCompanyDetails({ name: '', regNo: '', address: '', country: '', city: '', hotelId: '', departureDate: '', departureTime: '' });
      setMobile('');
      setCountry('');
      setCity('');
      setProfessionalInfo({ title: '', department: '' });
      setSpeakerOptions({ accommodation: false, airportPickup: false });
      setEmail('');
      setPortfolioLinks([]);
      setPortfolioInput('');
      setSelectedStaff(null);
      // Notify parent that a booking was added
      try {
        if (typeof onBookingAdded === 'function') onBookingAdded(data);
      } catch (e) {
        // ignore
      }
      // Send notification/email to the booker if an email was provided
      try {
        const bookerEmail = formData.get('email');
        if (bookerEmail) {
          await api.post('/api/notifications', {
            type: 'booking',
            user: bookerEmail,
            content: `Your booking has been created. Reference: ${data._id || data.id}`,
            relatedLink: `/booking/${data._id || data.id}`,
            sendEmail: true,
            bookingId: data._id || data.id
          });
        }
      } catch (e) {
        // non-fatal
        console.warn('Failed to send booking notification/email', e?.message || e);
      }
      // If this modal was opened as a manual/admin flow, close it
      try {
        if (typeof onClose === 'function') onClose();
      } catch (e) { }
    } catch (err) {
      // Axios error: err.response?.data?.error or err.message
      setErrorMsg((err.response && err.response.data && err.response.data.error) ? err.response.data.error : (err.message || 'Booking failed. Please check your internet connection or try again.'));
    }
  }
  // Icon map for package categories
  const CATEGORY_ICONS = {
    delegate: '🧑‍💼',
    exhibitor: '🏬',
    sponsorship: '🤝',
    speaker: '🎙️',
  };
  // Theme detection
  const themeContext = useContext(ThemeContext);
  const theme = themeContext?.theme || (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');

  // State hooks
  const [packages, setPackages] = useState([]);
  const [packagesError, setPackagesError] = useState("");
  const [step, setStep] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('delegate');
  const [selectedPackage, setSelectedPackage] = useState('');
  const [bulkCount, setBulkCount] = useState(1);
  const [docFile, setDocFile] = useState(null);
  const [docUrl, setDocUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Add explicit name field for clarity and backend compatibility
  const [companyDetails, setCompanyDetails] = useState({ name: '', regNo: '', address: '', country: '', city: '', hotelId: '', departureDate: '', departureTime: '' });
  const [mobile, setMobile] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [professionalInfo, setProfessionalInfo] = useState({ title: '', department: '' });
  const [speakerOptions, setSpeakerOptions] = useState({ accommodation: false, airportPickup: false });
  const [email, setEmail] = useState('');
  const [confirmation, setConfirmation] = useState(false);
  const [templateUrl, setTemplateUrl] = useState('');
  const [bookingId, setBookingId] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [portfolioLinks, setPortfolioLinks] = useState([]);
  const [portfolioInput, setPortfolioInput] = useState("");
  const [staffOptions, setStaffOptions] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [loadingStaff, setLoadingStaff] = useState(false);

  useEffect(() => {
    if (open) {
      getPackages()
        .then(data => {
          if (data && Array.isArray(data.packages)) {
            setPackages(data.packages);
            setPackagesError("");
          } else if (Array.isArray(data)) {
            setPackages(data);
            setPackagesError("");
          } else {
            setPackages([]);
            setPackagesError("No packages found.");
          }
        })
        .catch(() => {
          setPackages([]);
          setPackagesError("Failed to load packages. Please try again or contact support.");
        });
      setSelectedPackage('');
      // Fetch staff list for admin assignment (searchable select)
      if (currentUser && ['admin', 'superadmin'].includes(currentUser.role)) {
        setLoadingStaff(true);
        api.get('/api/users?role=staff')
          .then(res => {
            const payload = res.data || res;
            const users = Array.isArray(payload) ? payload : (payload.users || []);
            const opts = users.map(u => ({ value: u._id || u.id, label: (u.name || u.fullName || u.email) }));
            setStaffOptions(opts);
          })
          .catch(() => {
            setStaffOptions([]);
          })
          .finally(() => setLoadingStaff(false));
      }
      // Fetch template URL from CMS/settings
      getCMS().then(items => {
        // Look for a CMS item with type 'settings' or a custom type for templates
        let found = null;
        if (Array.isArray(items)) {
          found = items.find(i => i.type === 'settings' && i.contentJSON?.bookingTemplateUrl);
        }
        if (found) {
          setTemplateUrl(found.contentJSON.bookingTemplateUrl);
        } else {
          setTemplateUrl('/assets/company-details-template.pdf'); // fallback
        }
      }).catch(() => setTemplateUrl('/assets/company-details-template.pdf'));
    }
  }, [open]);

  // --- Portfolio Link tag input handlers ---
  function handlePortfolioInputChange(e) {
    setPortfolioInput(e.target.value);
  }
  function handlePortfolioInputKeyDown(e) {
    if ((e.key === 'Enter' || e.key === ',') && portfolioInput.trim()) {
      e.preventDefault();
      if (!portfolioLinks.includes(portfolioInput.trim())) {
        setPortfolioLinks([...portfolioLinks, portfolioInput.trim()]);
      }
      setPortfolioInput("");
    }
  }
  function handleRemovePortfolioLink(idx) {
    setPortfolioLinks(portfolioLinks.filter((_, i) => i !== idx));
  }

  // Derived values
  // Full country list for dropdown
  const COUNTRY_OPTIONS = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
    'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi',
    'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo, Democratic Republic of the', 'Congo, Republic of the', 'Costa Rica', 'Cote d\'Ivoire', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic',
    'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
    'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia',
    'Fiji', 'Finland', 'France',
    'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
    'Haiti', 'Honduras', 'Hungary',
    'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
    'Jamaica', 'Japan', 'Jordan',
    'Kazakhstan', 'Kenya', 'Kiribati', 'Korea, North', 'Korea, South', 'Kosovo', 'Kuwait', 'Kyrgyzstan',
    'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg',
    'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar',
    'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Macedonia', 'Norway',
    'Oman',
    'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
    'Qatar',
    'Romania', 'Russia', 'Rwanda',
    'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
    'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
    'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan',
    'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
    'Yemen',
    'Zambia', 'Zimbabwe',
    'Other'
  ];
  const COUNTRY_SELECT_OPTIONS = COUNTRY_OPTIONS.map(c => ({ value: c, label: c }));
  // City options for a few countries, fallback to 'Other'
  const CITY_MAP = {
    'Tanzania': ['Dar es Salaam', 'Arusha', 'Dodoma', 'Mwanza', 'Mbeya', 'Morogoro', 'Tanga', 'Moshi', 'Iringa', 'Shinyanga', 'Tabora', 'Singida', 'Mtwara', 'Songea', 'Musoma', 'Bukoba', 'Other'],
    'Kenya': ['Nairobi', 'Mombasa', 'Kisumu', 'Eldoret', 'Nakuru', 'Thika', 'Malindi', 'Kitale', 'Garissa', 'Other'],
    'Uganda': ['Kampala', 'Entebbe', 'Gulu', 'Lira', 'Mbarara', 'Jinja', 'Mbale', 'Other'],
    'Rwanda': ['Kigali', 'Butare', 'Gisenyi', 'Other'],
    'Burundi': ['Bujumbura', 'Gitega', 'Ngozi', 'Other'],
    'South Africa': ['Johannesburg', 'Cape Town', 'Durban', 'Pretoria', 'Port Elizabeth', 'Bloemfontein', 'Other'],
    'Nigeria': ['Lagos', 'Abuja', 'Kano', 'Ibadan', 'Port Harcourt', 'Benin City', 'Other'],
    'Ghana': ['Accra', 'Kumasi', 'Tamale', 'Takoradi', 'Other']
  };
  const [cityOptions, setCityOptions] = useState(['Other']);
  useEffect(() => {
    // Always show at least 'Other' for any country
    if (CITY_MAP[country]) {
      setCityOptions(CITY_MAP[country]);
    } else {
      setCityOptions(['Other']);
    }
    // Only reset city if the new country does not contain the current city
    if (!((CITY_MAP[country] || ['Other']).includes(city))) {
      setCity('');
    }
  }, [country]);
  {/* Mobile Number (required) */ }
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <label style={{ fontWeight: 700, fontSize: 15, color: '#00a679', display: 'flex', alignItems: 'center', gap: 6 }}>
      <span role="img" aria-label="Mobile">📱</span> Mobile Number <span style={{ color: 'red' }}>*</span>
    </label>
    <input type="tel" value={mobile} onChange={e => setMobile(e.target.value)} required placeholder="e.g. +255712345678" style={{ width: '100%', padding: 10, borderRadius: 8, border: '2.5px solid #00a679', fontSize: 15, background: theme === 'dark' ? '#23242a' : '#fff', color: theme === 'dark' ? '#f3f3f3' : '#222', fontWeight: 600, boxShadow: '0 0 0 2px rgba(0,166,121,0.15)' }} />
  </div>
  {/* Country Dropdown */ }
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <label style={{ fontWeight: 700, fontSize: 15, color: '#00a679', display: 'flex', alignItems: 'center', gap: 6 }}>
      <span role="img" aria-label="Country">🌍</span> Country <span style={{ color: 'red' }}>*</span>
    </label>
    <Select
      options={COUNTRY_SELECT_OPTIONS}
      value={country ? { value: country, label: country } : null}
      onChange={opt => { setCountry(opt ? opt.value : ''); setCity(''); }}
      placeholder="Select Country..."
      isClearable
      isSearchable
      styles={{
        control: (base) => ({
          ...base,
          border: '2.5px solid #00a679',
          borderRadius: 8,
          fontSize: 15,
          fontWeight: 600,
          background: theme === 'dark' ? '#23242a' : '#fff',
          color: theme === 'dark' ? '#f3f3f3' : '#222',
        }),
        menu: (base) => ({ ...base, zIndex: 9999 }),
      }}
    />
  </div>
  {/* City Dropdown */ }
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <label style={{ fontWeight: 700, fontSize: 15, color: '#00a679', display: 'flex', alignItems: 'center', gap: 6 }}>
      <span role="img" aria-label="City">🏙️</span> City <span style={{ color: 'red' }}>*</span>
    </label>
    <select value={city} onChange={e => setCity(e.target.value)} required style={{ width: '100%', padding: 10, borderRadius: 8, border: '2.5px solid #00a679', fontSize: 15, background: theme === 'dark' ? '#23242a' : '#fff', color: theme === 'dark' ? '#f3f3f3' : '#222', fontWeight: 600 }}>
      <option value="">Select City</option>
      {cityOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
    </select>
  </div>
  const availableCategories = getAvailableCategories(packages);
  // Group packages by category for display
  const groupedPackages = availableCategories.map(cat => ({
    ...cat,
    packages: packages.filter(p => p.category === cat.key)
  }));

  // Only one unconditional early return for modal visibility
  if (!open) return null;

  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: theme === 'dark' ? 'rgba(12,12,16,0.82)' : 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      transition: 'background 0.3s',
      padding: '20px',
      overflowY: 'auto',
    }}>
      <div
        style={{
          background: theme === 'dark' ? '#181a20' : '#fff',
          color: theme === 'dark' ? '#f3f3f3' : '#222',
          borderRadius: 18,
          padding: '32px 24px',
          minWidth: 0,
          width: '100%',
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: theme === 'dark' ? '0 8px 32px rgba(0,0,0,0.44)' : '0 8px 32px rgba(0,0,0,0.18)',
          position: 'relative',
          transition: 'all 0.3s',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
          border: theme === 'dark' ? '1.5px solid #23242a' : '1.5px solid #eee',
          margin: 'auto',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 16, right: 16,
            background: theme === 'dark' ? '#23242a' : '#f5f5f5',
            border: 'none', fontSize: 28, cursor: 'pointer', borderRadius: '50%', width: 40, height: 40,
            boxShadow: theme === 'dark' ? '0 1px 4px rgba(0,0,0,0.18)' : '0 1px 4px rgba(0,0,0,0.08)',
            color: theme === 'dark' ? '#f3f3f3' : '#222',
            transition: 'background 0.2s',
          }}
          aria-label="Close"
          onMouseOver={e => e.currentTarget.style.background = theme === 'dark' ? '#181a20' : '#e0e0e0'}
          onMouseOut={e => e.currentTarget.style.background = theme === 'dark' ? '#23242a' : '#f5f5f5'}
        >×</button>
        {/* Step 1: Choose Category */}
        {step === 1 && (
          <div
            style={{
              background: theme === 'dark' ? '#181a20' : '#fff',
              color: theme === 'dark' ? '#f3f3f3' : '#222',
              borderRadius: 14,
              padding: '18px 0 8px 0',
              boxShadow: theme === 'dark' ? '0 2px 16px rgba(0,0,0,0.28)' : 'none',
              marginBottom: 0,
              border: theme === 'dark' ? '1.5px solid #23242a' : '1.5px solid #eee',
              width: '100%',
              maxWidth: 480,
              margin: '0 auto',
            }}
          >
            <h2 style={{ textAlign: 'center', color: theme === 'dark' ? '#f3f3f3' : '#222', fontSize: '1.8rem', fontWeight: 900, marginBottom: 6, color: '#00a679' }}>Timte 2026 - Event Booking</h2>
            <h3 style={{ textAlign: 'center', color: theme === 'dark' ? '#b0b0b0' : '#555', fontSize: '0.95rem', fontWeight: 600, marginBottom: 20 }}>Choose Your Category</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: 18,
              marginBottom: 20,
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              maxWidth: 500,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}>
              {availableCategories.map(opt => (
                <button
                  type="button"
                  key={opt.key}
                  onClick={() => setSelectedCategory(opt.key)}
                  style={{
                    border: selectedCategory === opt.key ? '2.5px solid #00a679' : `1.5px solid ${theme === 'dark' ? '#444' : '#ccc'}`,
                    background: selectedCategory === opt.key ? (theme === 'dark' ? 'rgba(0,166,121,0.12)' : 'rgba(0,166,121,0.08)') : (theme === 'dark' ? '#181a20' : '#fff'),
                    color: theme === 'dark' ? '#f3f3f3' : '#222',
                    padding: '22px 20px', borderRadius: 16, cursor: 'pointer', minWidth: 120, minHeight: 110,
                    boxShadow: selectedCategory === opt.key ? (theme === 'dark' ? '0 2px 16px rgba(0,166,121,0.18)' : '0 2px 16px rgba(0,166,121,0.10)') : (theme === 'dark' ? '0 1px 8px rgba(0,0,0,0.18)' : '0 1px 8px rgba(0,0,0,0.08)'),
                    transition: 'all 0.2s',
                    outline: selectedCategory === opt.key ? 'none' : undefined,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  }}
                  aria-pressed={selectedCategory === opt.key}
                >
                  <span style={{ fontSize: 40, marginBottom: 8, lineHeight: 1, color: theme === 'dark' ? '#f3f3f3' : '#222' }}>{opt.icon}</span>
                  <div style={{ fontSize: 18, fontWeight: 600, marginTop: 8, color: theme === 'dark' ? '#f3f3f3' : '#222' }}>{opt.label}</div>
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24, gap: 12 }}>
              <button type="button" style={{ flex: 1, padding: '12px 0', borderRadius: 8, border: 'none', background: theme === 'dark' ? '#23242a' : '#f5f5f5', color: theme === 'dark' ? '#f3f3f3' : '#222', fontWeight: 500, fontSize: 16, cursor: 'pointer', transition: 'background 0.2s' }} onClick={onClose}>Cancel</button>
              <button type="button" style={{ flex: 1, padding: '12px 0', borderRadius: 8, border: 'none', background: '#00a679', color: '#fff', fontWeight: 500, fontSize: 16, cursor: 'pointer', transition: 'background 0.2s', opacity: selectedCategory ? 1 : 0.6 }} onClick={() => setStep(2)} disabled={!selectedCategory}>Next</button>
            </div>
          </div>
        )}
        {/* Step 2: Select Package (Only Selected Category) */}
        {step === 2 && (
          <form
            onSubmit={e => { e.preventDefault(); setStep(3); }}
            style={{
              maxWidth: 520,
              width: '100%',
              margin: '0 auto',
              background: 'none',
              padding: 0,
              display: 'flex', flexDirection: 'column', gap: 0,
            }}
          >
            <h2 style={{ textAlign: 'center', marginBottom: 18, color: theme === 'dark' ? '#f3f3f3' : '#222' }}>Select Package</h2>
            {packagesError ? (
              <div style={{ color: 'red', textAlign: 'center', marginBottom: 18 }}>{packagesError}</div>
            ) : null}
            {(() => {
              // Only show speaker packages if speaker selected
              if (selectedCategory === 'speaker') {
                const speakerPkgs = getSpeakerPackages(packages);
                if (speakerPkgs.length === 0) {
                  return <div style={{ color: 'red', textAlign: 'center', marginBottom: 18 }}>No speaker packages available.</div>;
                }
                return (
                  <div key="speaker" style={{ marginBottom: 18 }}>
                    <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 22 }}>🎤</span> Speaker
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                      gap: 18,
                    }}>
                      {speakerPkgs.map(pkg => (
                        <div
                          key={pkg._id}
                          onClick={() => setSelectedPackage(pkg._id)}
                          style={{
                            border: selectedPackage === pkg._id ? '2.5px solid #00a679' : `1.5px solid ${theme === 'dark' ? '#444' : '#ccc'}`,
                            background: selectedPackage === pkg._id ? (theme === 'dark' ? '#23242a' : '#e6f0ff') : (theme === 'dark' ? '#181a20' : '#fff'),
                            color: theme === 'dark' ? '#f3f3f3' : '#222',
                            borderRadius: 18,
                            padding: '22px 18px 18px 18px',
                            cursor: 'pointer',
                            minHeight: 120,
                            boxShadow: selectedPackage === pkg._id
                              ? (theme === 'dark' ? '0 2px 16px rgba(0,123,255,0.18)' : '0 2px 16px rgba(0,123,255,0.10)')
                              : (theme === 'dark' ? '0 1px 8px rgba(0,0,0,0.18)' : '0 1px 8px rgba(0,0,0,0.08)'),
                            transition: 'all 0.2s',
                            outline: selectedPackage === pkg._id ? 'none' : undefined,
                            display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center',
                            position: 'relative',
                          }}
                          tabIndex={0}
                          aria-pressed={selectedPackage === pkg._id}
                        >
                          <span style={{
                            fontSize: 38,
                            marginBottom: 8,
                            lineHeight: 1,
                            color: selectedPackage === pkg._id ? '#00a679' : (theme === 'dark' ? '#f3f3f3' : '#00a679'),
                            alignSelf: 'center',
                          }}>🎤</span>
                          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>{pkg.name}</div>
                          <div style={{ fontSize: 15, marginBottom: 8, color: theme === 'dark' ? '#b0b0b0' : '#555' }}>{pkg.description}</div>
                          <div style={{ fontSize: 18, fontWeight: 600, color: '#00a679', marginTop: 'auto' }}>${pkg.price}</div>
                          {selectedPackage === pkg._id && (
                            <span style={{
                              position: 'absolute', top: 8, right: 8, fontSize: 22, color: '#00a679',
                            }}>✔️</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              // Default for other categories
              const cat = groupedPackages.find(c => c.key === selectedCategory);
              if (!cat || cat.packages.length === 0) {
                return <div style={{ color: 'red', textAlign: 'center', marginBottom: 18 }}>No packages available for this category.</div>;
              }
              return (
                <div key={cat.key} style={{ marginBottom: 18 }}>
                  <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 22 }}>{cat.icon}</span> {cat.label}
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: 18,
                  }}>
                    {cat.packages.map(pkg => (
                      <div
                        key={pkg._id}
                        onClick={() => setSelectedPackage(pkg._id)}
                        style={{
                          border: selectedPackage === pkg._id ? '2.5px solid #00a679' : `1.5px solid ${theme === 'dark' ? '#444' : '#ccc'}`,
                          background: selectedPackage === pkg._id ? (theme === 'dark' ? '#23242a' : '#e6f0ff') : (theme === 'dark' ? '#181a20' : '#fff'),
                          color: theme === 'dark' ? '#f3f3f3' : '#222',
                          borderRadius: 18,
                          padding: '22px 18px 18px 18px',
                          cursor: 'pointer',
                          minHeight: 120,
                          boxShadow: selectedPackage === pkg._id
                            ? (theme === 'dark' ? '0 2px 16px rgba(0,123,255,0.18)' : '0 2px 16px rgba(0,123,255,0.10)')
                            : (theme === 'dark' ? '0 1px 8px rgba(0,0,0,0.18)' : '0 1px 8px rgba(0,0,0,0.08)'),
                          transition: 'all 0.2s',
                          outline: selectedPackage === pkg._id ? 'none' : undefined,
                          display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center',
                          position: 'relative',
                        }}
                        tabIndex={0}
                        aria-pressed={selectedPackage === pkg._id}
                      >
                        <span style={{
                          fontSize: 38,
                          marginBottom: 8,
                          lineHeight: 1,
                          color: selectedPackage === pkg._id ? '#00a679' : (theme === 'dark' ? '#f3f3f3' : '#00a679'),
                          alignSelf: 'center',
                        }}>{CATEGORY_ICONS[pkg.category] || '📦'}</span>
                        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>{pkg.name}</div>
                        <div style={{ fontSize: 15, marginBottom: 8, color: theme === 'dark' ? '#b0b0b0' : '#555' }}>{pkg.description}</div>
                        <div style={{ fontSize: 18, fontWeight: 600, color: '#00a679', marginTop: 'auto' }}>${pkg.price}</div>
                        {selectedPackage === pkg._id && (
                          <span style={{
                            position: 'absolute', top: 8, right: 8, fontSize: 22, color: '#00a679',
                          }}>✔️</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 10 }}>
              <button type="button" onClick={() => setStep(1)} style={{ flex: 1, padding: '12px 0', borderRadius: 8, border: 'none', background: theme === 'dark' ? '#23242a' : '#f5f5f5', color: theme === 'dark' ? '#f3f3f3' : '#222', fontWeight: 500, fontSize: 16, cursor: 'pointer', transition: 'background 0.2s' }}>Back</button>
              <button type="button" onClick={onClose} style={{ flex: 1, padding: '12px 0', borderRadius: 8, border: 'none', background: theme === 'dark' ? '#23242a' : '#f5f5f5', color: theme === 'dark' ? '#f3f3f3' : '#222', fontWeight: 500, fontSize: 16, cursor: 'pointer', transition: 'background 0.2s' }}>Cancel</button>
              <button type="submit" style={{ flex: 1, padding: '12px 0', borderRadius: 8, border: 'none', background: '#00a679', color: '#fff', fontWeight: 500, fontSize: 16, cursor: 'pointer', transition: 'background 0.2s', opacity: selectedPackage ? 1 : 0.6 }} disabled={!selectedPackage}>Next</button>
            </div>
          </form>
        )}
        {/* Step 3: Booking Details (Category/Package Adaptive) */}
        {step === 3 && (() => {
          const pkg = packages.find(p => p._id === selectedPackage);
          if (!pkg) return null;
          return (
            <form onSubmit={handleSubmit} style={{
              maxWidth: '100%',
              background: theme === 'dark' ? '#181a20' : '#f9f9fb',
              borderRadius: 20,
              padding: '0',
              margin: '0 auto',
              boxShadow: 'none',
              width: '100%',
              minWidth: 0,
              display: 'grid',
              gridTemplateColumns: window.innerWidth > 900 ? '1fr 1fr' : '1fr',
              gap: 20,
              rowGap: 14,
            }}>
              <input type="hidden" name="bookingType" value={pkg.category} />
              <div style={{
                gridColumn: '1/-1',
                background: theme === 'dark' ? 'rgba(0,123,255,0.08)' : 'rgba(0,123,255,0.06)',
                border: `2px solid ${theme === 'dark' ? 'rgba(0,123,255,0.24)' : 'rgba(0,123,255,0.16)'}`,
                borderRadius: 16,
                padding: '20px',
                marginBottom: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 36 }}>📦</span>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: 20, fontWeight: 800, color: theme === 'dark' ? '#f3f3f3' : '#111' }}>{pkg.name}</h3>
                    <p style={{ margin: '0', fontSize: 14, color: theme === 'dark' ? '#b0b0b0' : '#666', fontStyle: 'italic' }}>{pkg.description}</p>
                  </div>
                  <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <div style={{ fontSize: 28, fontWeight: 900, color: '#00a679' }}>${pkg.price}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: theme === 'dark' ? '#b0b0b0' : '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Per {pkg.category}</div>
                  </div>
                </div>
              </div>
              <h2 style={{
                gridColumn: '1/-1',
                textAlign: 'center',
                marginBottom: '12px',
                marginTop: '0',
                color: theme === 'dark' ? '#f3f3f3' : '#222',
                fontSize: 24,
                fontWeight: 800,
                letterSpacing: 0.5
              }}>Complete Your {pkg.category.charAt(0).toUpperCase() + pkg.category.slice(1)} {pkg.category === 'speaker' ? 'Registration' : 'Booking'}</h2>

              {/* Booker Name / Company Name (required) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, gridColumn: '1/-1' }}>
                <label style={{ fontWeight: 600, fontSize: 15, display: 'flex', alignItems: 'center', gap: 6 }}><span role="img" aria-label="User">👤</span> Booker Name / Company Name <span style={{ color: 'red' }}>*</span></label>
                <input type="text" value={companyDetails.name} onChange={e => setCompanyDetails({ ...companyDetails, name: e.target.value })} required placeholder="Full Name or Company Name" style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid #bbb', fontSize: 15, background: theme === 'dark' ? '#23242a' : '#fff', color: theme === 'dark' ? '#f3f3f3' : '#222', fontWeight: 500 }} />
              </div>

              {/* Email (required) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <label style={{ fontWeight: 700, fontSize: 15, color: '#d32f2f', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span role="img" aria-label="Important">❗</span> Email <span style={{ color: 'red' }}>*</span>
                </label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="Email" style={{ width: '100%', padding: 10, borderRadius: 8, border: '2.5px solid #d32f2f', fontSize: 15, background: theme === 'dark' ? '#23242a' : '#fff', color: theme === 'dark' ? '#f3f3f3' : '#222', fontWeight: 600, boxShadow: '0 0 0 2px #ffd6d6' }} />
              </div>

              {/* Mobile Number (required) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <label style={{ fontWeight: 700, fontSize: 15, color: '#00a679', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span role="img" aria-label="Mobile">📱</span> Mobile Number <span style={{ color: 'red' }}>*</span>
                </label>
                <input type="tel" value={mobile} onChange={e => setMobile(e.target.value)} required placeholder="e.g. +255712345678" style={{ width: '100%', padding: 10, borderRadius: 8, border: '2.5px solid #00a679', fontSize: 15, background: theme === 'dark' ? '#23242a' : '#fff', color: theme === 'dark' ? '#f3f3f3' : '#222', fontWeight: 600, boxShadow: '0 0 0 2px rgba(0,166,121,0.15)' }} />
              </div>

              {/* Country Dropdown (required) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <label style={{ fontWeight: 700, fontSize: 15, color: '#00a679', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span role="img" aria-label="Country">🌍</span> Country <span style={{ color: 'red' }}>*</span>
                </label>
                <select value={country} onChange={e => { setCountry(e.target.value); setCity(''); }} required style={{ width: '100%', padding: 10, borderRadius: 8, border: '2.5px solid #00a679', fontSize: 15, background: theme === 'dark' ? '#23242a' : '#fff', color: theme === 'dark' ? '#f3f3f3' : '#222', fontWeight: 600 }}>
                  <option value="">Select Country</option>
                  {COUNTRY_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>

                {/* Reset city when country changes to force user to pick from new city list */}
                {/* This effect ensures city is always valid for the selected country */}
                {typeof window !== 'undefined' && window.React && React.useEffect && React.useEffect(() => {
                  setCity('');
                }, [country])}
              </div>

              {/* City Dropdown (required, always visible, always valid) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <label style={{ fontWeight: 700, fontSize: 15, color: '#00a679', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span role="img" aria-label="City">🏙️</span> City <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  value={city}
                  onChange={e => setCity(e.target.value)}
                  required
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '2.5px solid #00a679', fontSize: 15, background: theme === 'dark' ? '#23242a' : '#fff', color: theme === 'dark' ? '#f3f3f3' : '#222', fontWeight: 600 }}
                >
                  <option value="">Select City</option>
                  {cityOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              {/* Number of People (hide for speaker) */}
              {pkg.category !== 'speaker' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <label style={{ fontWeight: 600, fontSize: 14 }}>Number of People</label>
                  <input type="number" min={1} value={bulkCount} onChange={e => setBulkCount(Number(e.target.value))} placeholder="Number" style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid #bbb', fontSize: 15, background: theme === 'dark' ? '#23242a' : '#fff', color: theme === 'dark' ? '#f3f3f3' : '#222', fontWeight: 500 }} />
                </div>
              )}
              {/* Add-ons: Only for non-speaker */}
              {pkg.category !== 'speaker' && (
                <div style={{ gridColumn: '1/-1', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', margin: '6px 0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14, cursor: 'pointer', background: speakerOptions.accommodation ? (theme === 'dark' ? '#23242a' : '#e6f0ff') : 'transparent', borderRadius: 6, padding: '4px 10px', border: speakerOptions.accommodation ? '2px solid #00a679' : `1px solid ${theme === 'dark' ? '#444' : '#ccc'}` }}>
                    <input type="checkbox" checked={speakerOptions.accommodation} onChange={e => setSpeakerOptions({ ...speakerOptions, accommodation: e.target.checked })} style={{ accentColor: '#00a679' }} />
                    <span style={{ fontSize: 16 }}>🏨</span> Accommodation
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14, cursor: 'pointer', background: speakerOptions.airportPickup ? (theme === 'dark' ? '#23242a' : '#e6f0ff') : 'transparent', borderRadius: 6, padding: '4px 10px', border: speakerOptions.airportPickup ? '2px solid #00a679' : `1px solid ${theme === 'dark' ? '#444' : '#ccc'}` }}>
                    <input type="checkbox" checked={speakerOptions.airportPickup} onChange={e => setSpeakerOptions({ ...speakerOptions, airportPickup: e.target.checked })} style={{ accentColor: '#00a679' }} />
                    <span style={{ fontSize: 16 }}>✈️</span> Airport Pickup
                  </label>
                </div>
              )}
              {/* Speaker-specific fields */}
              {pkg.category === 'speaker' && (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, gridColumn: '1/-1' }}>
                    <label style={{ fontWeight: 600, fontSize: 14 }}>Portfolio Link(s) <span style={{ color: 'red' }}>*</span></label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, minHeight: 38, alignItems: 'center', background: theme === 'dark' ? '#23242a' : '#f5f5f5', border: '2px solid #bbb', borderRadius: 8, padding: 6 }}>
                      {portfolioLinks.map((link, idx) => (
                        <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', background: '#00a679', color: '#fff', borderRadius: 6, padding: '2px 8px', fontSize: 13, marginRight: 4 }}>
                          {link}
                          <button type="button" onClick={() => handleRemovePortfolioLink(idx)} style={{ marginLeft: 6, background: 'none', border: 'none', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 15, lineHeight: 1 }} aria-label="Remove">×</button>
                        </span>
                      ))}
                      <input
                        type="url"
                        placeholder="Paste or type link and press Enter"
                        value={portfolioInput}
                        onChange={handlePortfolioInputChange}
                        onKeyDown={handlePortfolioInputKeyDown}
                        style={{ minWidth: 180, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: theme === 'dark' ? '#f3f3f3' : '#222' }}
                        required={portfolioLinks.length === 0}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, gridColumn: '1/-1' }}>
                    <label style={{ fontWeight: 600, fontSize: 14 }}>Speaker Details <span style={{ color: 'red' }}>*</span></label>
                    <textarea required placeholder="Speaker details, topics, experience, etc." value={professionalInfo.details || ''} onChange={e => setProfessionalInfo({ ...professionalInfo, details: e.target.value })} style={{ width: '100%', minHeight: 80, padding: 10, borderRadius: 8, border: '2px solid #bbb', fontSize: 15, background: theme === 'dark' ? '#23242a' : '#fff', color: theme === 'dark' ? '#f3f3f3' : '#222' }} />
                  </div>
                </>
              )}
              {/* Company/Professional Info */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <input placeholder="Company Name" value={companyDetails.name} onChange={e => setCompanyDetails({ ...companyDetails, name: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid #bbb', fontSize: 15, background: theme === 'dark' ? '#23242a' : '#fff', color: theme === 'dark' ? '#f3f3f3' : '#222' }} />
              </div>
              {/* Registration No. removed for speaker */}
              {pkg.category !== 'speaker' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <input placeholder="Registration No." value={companyDetails.regNo} onChange={e => setCompanyDetails({ ...companyDetails, regNo: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid #bbb', fontSize: 15, background: theme === 'dark' ? '#23242a' : '#fff', color: theme === 'dark' ? '#f3f3f3' : '#222' }} />
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, gridColumn: '1/-1' }}>
                <input placeholder="Address" value={companyDetails.address} onChange={e => setCompanyDetails({ ...companyDetails, address: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid #bbb', fontSize: 15, background: theme === 'dark' ? '#23242a' : '#fff', color: theme === 'dark' ? '#f3f3f3' : '#222' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <input placeholder="Title/Position" value={professionalInfo.title} onChange={e => setProfessionalInfo({ ...professionalInfo, title: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid #bbb', fontSize: 15, background: theme === 'dark' ? '#23242a' : '#fff', color: theme === 'dark' ? '#f3f3f3' : '#222' }} />
              </div>
              {/* Department removed for speaker */}
              {pkg.category !== 'speaker' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <input placeholder="Department" value={professionalInfo.department} onChange={e => setProfessionalInfo({ ...professionalInfo, department: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid #bbb', fontSize: 15, background: theme === 'dark' ? '#23242a' : '#fff', color: theme === 'dark' ? '#f3f3f3' : '#222' }} />
                </div>
              )}
              {/* Assign staff (admin only) */}
              {currentUser && ['admin', 'superadmin'].includes(currentUser.role) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, gridColumn: '1/-1' }}>
                  <label style={{ fontWeight: 700, fontSize: 14 }}>Assign Staff (optional)</label>
                  <div style={{ minWidth: 200 }}>
                    <Select
                      isClearable
                      isLoading={loadingStaff}
                      options={staffOptions}
                      value={selectedStaff}
                      onChange={opt => setSelectedStaff(opt)}
                      placeholder="Search staff by name or email"
                      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    />
                  </div>
                </div>
              )}
              {/* Show package price and add-ons summary */}
              <div style={{ marginBottom: 0, fontWeight: 600, fontSize: 15, color: theme === 'dark' ? '#f3f3f3' : '#222', gridColumn: '1/-1' }}>
                Package Price: <span style={{ color: '#00a679' }}>${pkg.price}</span>
              </div>
              {/* Document upload: Only show for non-speaker, for speaker use upload button at bottom */}
              {pkg.category !== 'speaker' && (
                <div style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0' }}>
                  <label style={{ fontWeight: 600, fontSize: 14, marginRight: 8 }}>
                    Company or Personal Details (PDF)
                    <input
                      id="company-details-upload"
                      type="file"
                      accept="application/pdf"
                      onChange={e => setDocFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                      style={{ display: 'none' }}
                    />
                    <span
                      style={{
                        display: 'inline-block',
                        marginLeft: 10,
                        padding: '6px 14px',
                        background: theme === 'dark' ? '#23242a' : '#f5f5f5',
                        border: '2px solid #bbb',
                        borderRadius: 8,
                        color: '#00a679',
                        cursor: 'pointer',
                        fontWeight: 500
                      }}
                      onClick={e => {
                        e.preventDefault();
                        document.getElementById('company-details-upload').click();
                      }}
                    >Upload</span>
                  </label>
                  <a
                    href={templateUrl || '/assets/company-details-template.pdf'}
                    download
                    style={{ fontSize: 13, color: '#00a679', textDecoration: 'underline', fontWeight: 500 }}
                    title="Download company/personal details template"
                  >Download template</a>
                  <button type="button" onClick={handleDocUpload} disabled={!docFile || uploading} style={{ marginLeft: 8, padding: '6px 14px', borderRadius: 8, border: 'none', background: '#00a679', color: '#fff', fontWeight: 500, fontSize: 14, cursor: docFile && !uploading ? 'pointer' : 'not-allowed', opacity: docFile && !uploading ? 1 : 0.6, display: 'inline-block' }}>
                    {uploading ? 'Uploading...' : 'Upload File'}
                  </button>
                  {docUrl && <a href={docUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#00a679', textDecoration: 'underline', fontWeight: 500, marginLeft: 6 }}>View</a>}
                </div>
              )}
              {/* Speaker upload button (above actions) */}
              {pkg.category === 'speaker' && (
                <div style={{ gridColumn: '1/-1', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '10px 0 18px 0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#f5f5f5', borderRadius: '50%', width: 44, height: 44, border: '1.5px solid #bbb', margin: 0 }} title="Upload CV/Portfolio (PDF)">
                    <input
                      id="speaker-cv-upload"
                      type="file"
                      accept="application/pdf"
                      required
                      onChange={e => setDocFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                      style={{ display: 'none' }}
                    />
                    <span
                      style={{ fontSize: 24, color: '#00a679', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44 }}
                      onClick={e => {
                        e.preventDefault();
                        document.getElementById('speaker-cv-upload').click();
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24"><path fill="#00a679" d="M16.5 6.5l-9 9a2.121 2.121 0 003 3l9-9a2.121 2.121 0 00-3-3zm-7.09 10.5a.75.75 0 01-1.06-1.06l9-9a.75.75 0 111.06 1.06l-9 9z"></path></svg>
                    </span>
                  </label>
                  <button type="button" onClick={handleDocUpload} disabled={!docFile || uploading} style={{ marginLeft: 8, padding: '6px 14px', borderRadius: 8, border: 'none', background: '#00a679', color: '#fff', fontWeight: 500, fontSize: 14, cursor: docFile && !uploading ? 'pointer' : 'not-allowed', opacity: docFile && !uploading ? 1 : 0.6, display: 'inline-block' }}>
                    {uploading ? 'Uploading...' : 'Upload File'}
                  </button>
                  {docFile && <span style={{ marginLeft: 12, fontSize: 14, color: theme === 'dark' ? '#f3f3f3' : '#222', fontWeight: 500 }}>{docFile.name}</span>}
                </div>
              )}
              {/* Actions (Back, Cancel, Submit) */}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 6, gridColumn: '1/-1' }}>
                <button type="button" onClick={() => setStep(2)} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: theme === 'dark' ? '#23242a' : '#f5f5f5', color: theme === 'dark' ? '#f3f3f3' : '#222', fontWeight: 600, fontSize: 15, cursor: 'pointer', transition: 'background 0.2s' }}>Back</button>
                <button type="button" onClick={onClose} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: theme === 'dark' ? '#23242a' : '#f5f5f5', color: theme === 'dark' ? '#f3f3f3' : '#222', fontWeight: 600, fontSize: 15, cursor: 'pointer', transition: 'background 0.2s' }}>Cancel</button>
                <button type="submit" style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: '#00a679', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer', transition: 'background 0.2s' }}>{pkg.category === 'speaker' ? 'Submit' : 'Book'}</button>
              </div>
            </form>
          );
        })()}
        {/* Step 4: Confirmation */}
        {confirmation && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <h2 style={{ color: '#00a679', marginBottom: 12 }}>Booking Submitted!</h2>
            <p style={{ fontSize: 17, marginBottom: 8 }}>Thank you. Your booking was received.</p>
            {bookingId && (
              <div style={{ fontSize: 16, margin: '12px 0', color: '#222', fontWeight: 600 }}>
                Booking ID: <span style={{ color: '#00a679' }}>{bookingId}</span>
              </div>
            )}
            <p style={{ fontSize: 15, color: '#555' }}>A confirmation email has been sent to <b>{email}</b>.</p>
            <button onClick={onClose} style={{ marginTop: 20, padding: '10px 28px', borderRadius: 8, background: '#00a679', color: '#fff', border: 'none', fontWeight: 600, fontSize: 16, cursor: 'pointer' }}>Close</button>
          </div>
        )}
        {errorMsg && !confirmation && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <h2 style={{ color: '#ec2127', marginBottom: 12 }}>Booking Failed</h2>
            <p style={{ fontSize: 16, color: '#b00', marginBottom: 10 }}>{errorMsg}</p>
            <button onClick={() => setErrorMsg("")} style={{ marginTop: 20, padding: '10px 28px', borderRadius: 8, background: '#00a679', color: '#fff', border: 'none', fontWeight: 600, fontSize: 16, cursor: 'pointer' }}>Try Again</button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export function BookingButton() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <button onClick={() => setModalOpen(true)}>Open Booking</button>
      <BookingModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}
